import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ExternalLink } from "lucide-react";

const milestones = [
  { year: "1993", event: "Joins PwC as Assistant Staff Auditor" },
  { year: "2015", event: "Appointed US Chief Digital Officer" },
  { year: "2019", event: "Vice Chair, Chief Products & Technology Officer" },
  { year: "2023", event: "Architects ChatGPT Enterprise agreement with OpenAI" },
  { year: "2024", event: "Named Global Chief AI Officer, PwC" },
];

export default function AboutSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} id="about-bio" className="py-24 md:py-36 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A1E2B]/20 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-4">The Story</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl leading-tight">
            Three decades in the making.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-[#C4963B]/[0.03] blur-2xl rounded-full" />
              <img
                src="/joe-panel.jpg"
                alt="Joe Atkinson on panel with NVIDIA, UNIDO and global leaders discussing AI's role in economic growth"
                className="relative w-full object-cover rounded-sm"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121418]/70 to-transparent rounded-sm" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C4963B] font-semibold mb-1">Panel Discussion</p>
                <p className="text-sm text-[#E8E6E1]/70 font-['Cormorant_Garamond'] italic leading-snug">
                  "AI's Role in Accelerating Sustainable Economic Growth"
                </p>
                <p className="text-xs text-[#E8E6E1]/40 mt-1">with NVIDIA, UNIDO · Supported by PwC</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="space-y-5 text-[#E8E6E1]/55 text-base md:text-lg leading-relaxed mb-12">
              <p>
                Joe Atkinson began his career in 1993 as an assistant staff auditor at PwC. What
                followed was not a straight line to the top—it was a deliberate, 33-year immersion
                in what makes enterprises actually work: their people, their risk tolerance, and
                their capacity for change.
              </p>
              <p>
                As US Chief Digital Officer, Joe built the digital infrastructure that would later
                serve as the launchpad for AI at massive scale. As Vice Chair of Products and
                Technology, he oversaw portfolios spanning cloud, data, and emerging technologies.
                And as Global Chief AI Officer from 2024, he has directed a $1 billion generative
                AI investment strategy and negotiated PwC's landmark position as the world's
                largest primary customer of ChatGPT Enterprise.
              </p>
              <p>
                His philosophy is simple, proven, and rare: <span className="text-[#F9F6F0]">
                technology is the easy part. The hard part is equipping your people to use it,
                trust it, and grow with it.</span> That's what separates the 20% who capture 74%
                of AI returns from everyone else.
              </p>
            </div>

            <div className="space-y-0 border-l border-white/[0.06] pl-8">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-6">Career Timeline</p>
              {milestones.map((m, i) => (
                <motion.div
                  key={m.year}
                  className="relative pb-6 last:pb-0"
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                >
                  <div className="absolute -left-[33px] top-1 w-2 h-2 rounded-full bg-[#C4963B]/40 border border-[#C4963B]/60" />
                  <div className="flex items-baseline gap-4">
                    <span className="font-['Cormorant_Garamond'] text-[#C4963B] text-lg font-light shrink-0">{m.year}</span>
                    <span className="text-sm text-[#E8E6E1]/50 leading-snug">{m.event}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 pt-10 border-t border-white/[0.06] flex flex-wrap gap-4">
              <a
                href="https://www.linkedin.com/in/joe-atkinson-pwc/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#C4963B]/80 hover:text-[#C4963B] transition-colors border border-[#C4963B]/30 hover:border-[#C4963B] px-5 py-2.5"
              >
                LinkedIn Profile <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                href="https://www.pwc.com/gx/en/about/leadership/joe-atkinson.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-[#E8E6E1]/40 hover:text-[#C4963B] transition-colors border border-white/[0.08] hover:border-[#C4963B]/30 px-5 py-2.5"
              >
                PwC Profile <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
